import React, { useState, useEffect } from 'react';
import { api } from './api/api';

const humanizePart = (part) => part.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());

const gainColor = (v) => v > 0 ? '#16a34a' : v < 0 ? 'var(--c-error)' : undefined;

const RecList = ({ title, items, arrowLabel }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-text-muted)', marginBottom: 6 }}>{title}</div>
    {items.length === 0 ? (
      <div style={{ fontSize: 12, color: 'var(--c-text-light)' }}>None.</div>
    ) : (
      <table className="fb-table fb-table-sm">
        <thead><tr><th>Part</th><th>{arrowLabel}</th><th>Gain/lap</th><th>Gain/race</th></tr></thead>
        <tbody>
          {items.map(it => (
            <tr key={it.part}>
              <td style={{ fontWeight: 600 }}>{humanizePart(it.part)}</td>
              <td>{it.currentLevel} → {it.recommendedLevel}</td>
              <td style={{ color: gainColor(it.gainSecPerLap) }}>{it.gainSecPerLap?.toFixed(3)}s</td>
              <td style={{ color: gainColor(it.gainSecPerRace) }}>{it.gainSecPerRace?.toFixed(3)}s</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const RecommendUpgradeModal = ({ trackId, trackName, car, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.post(`/track/${trackId}/recommend-upgrade`, { car });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.status === 404
            ? 'No PHA/gains data for this track yet.'
            : err.response?.status === 403 ? 'Access denied.' : 'Failed to load recommendation.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [trackId, car]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 'var(--radius-lg)',
        padding: 24, width: 560, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Recommend Upgrade — {trackName ?? `Track #${trackId}`}</div>
          <button onClick={onClose} style={{
            padding: '6px 14px', borderRadius: 'var(--radius-sm)',
            background: '#f9fafb', border: '1px solid var(--c-border)',
            color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 13,
          }}>Close</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--c-text-light)', marginBottom: 14 }}>
          Theoretical max — ignores wear. See the ★ icons in Car Wear for wear-aware advice.
        </div>

        {loading && <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Calculating…</div>}
        {!loading && error && <div style={{ color: 'var(--c-error)', fontSize: 13 }}>{error}</div>}
        {!loading && !error && data && (
          <>
            <RecList title="Raise (+1 level)" items={data.raise || []} arrowLabel="Level" />
            <RecList title="Lower (to level 1)" items={data.lower || []} arrowLabel="Level" />
            <div style={{ fontSize: 13, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--c-border-light)' }}>
              Total if all applied: <strong style={{ color: gainColor(data.totalGainSecPerLap) }}>{data.totalGainSecPerLap?.toFixed(3)}s/lap</strong>
              {' · '}
              <strong style={{ color: gainColor(data.totalGainSecPerRace) }}>{data.totalGainSecPerRace?.toFixed(3)}s/race</strong>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecommendUpgradeModal;
